import { useCallback } from "react";
import ExcelJS from "exceljs";
import useGetAllProducts from "../../../hooks/useGetAllProducts";
import { HgProduct } from "../../../types/taxonomy";

type XLSDisplayProduct = {
  vendorId: string;
  vendorName: string;
  vendorUrl: string;
  productName: string;
  productId: string;
  productCategoryId: string;
  productCategoryLevel1Name: string;
  productCategoryLevel2Name: string;
  productCategoryLevel3Name: string;
  productCategoryLevel4Name: string;
  productCategoryLevel5Name: string;
  attributes: string;
  isCloudProduct: boolean;
  isOnPremiseProduct: boolean;
  isMobileProduct: boolean;
  productDescription: string;
};

function parseDisplayProduct(product: HgProduct): XLSDisplayProduct {
  return {
    vendorId: product.vendor?.id.toString() ?? "",
    vendorName: product.vendor?.name ?? "",
    vendorUrl: product.vendor?.url ?? "",
    productName: product.name,
    productId: product.id.toString(),
    productCategoryId: product.category?.code ?? "",
    productCategoryLevel1Name: product.category?.nameTree[0] ?? "",
    productCategoryLevel2Name: product.category?.nameTree[1] ?? "",
    productCategoryLevel3Name: product.category?.nameTree[2] ?? "",
    productCategoryLevel4Name: product.category?.nameTree[3] ?? "",
    productCategoryLevel5Name: product.category?.nameTree[4] ?? "",
    attributes: product.attributes?.map((a) => a.name).join("; ") ?? "",
    isCloudProduct: product.isCloudProduct || false,
    isOnPremiseProduct: product.isOnPremiseProduct || false,
    isMobileProduct: product.isMobileProduct || false,
    productDescription: product.description ?? "",
  };
}

export const useProductCatalogDownload = () => {
  const { products, loading } = useGetAllProducts();

  const downloadHandler = useCallback(async () => {
    const formattedProducts = products.map(parseDisplayProduct) ?? [];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("products");

    worksheet.columns = [
      { header: "vendor_id", key: "vendorId", width: 15 },
      { header: "vendor_name", key: "vendorName", width: 30 },
      { header: "vendor_url", key: "vendorUrl", width: 30 },
      { header: "product_name", key: "productName", width: 30 },
      { header: "product_id", key: "productId", width: 15 },
      { header: "product_category_id", key: "productCategoryId", width: 20 },
      {
        header: "product_category_level_1_name",
        key: "productCategoryLevel1Name",
        width: 25,
      },
      {
        header: "product_category_level_2_name",
        key: "productCategoryLevel2Name",
        width: 25,
      },
      {
        header: "product_category_level_3_name",
        key: "productCategoryLevel3Name",
        width: 25,
      },
      {
        header: "product_category_level_4_name",
        key: "productCategoryLevel4Name",
        width: 25,
      },
      {
        header: "product_category_level_5_name",
        key: "productCategoryLevel5Name",
        width: 25,
      },
      { header: "attributes", key: "attributes", width: 40 },
      { header: "is_cloud_product", key: "isCloudProduct", width: 18 },
      { header: "is_on_premise_product", key: "isOnPremiseProduct", width: 22 },
      { header: "is_mobile_product", key: "isMobileProduct", width: 18 },
      { header: "product_description", key: "productDescription", width: 50 },
    ];

    worksheet.addRows(formattedProducts);

    const date = new Date();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `HG Product Catalog ${month} ${year}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [products]);

  return { loading, callback: downloadHandler };
};
